'use client';

import clsx from 'clsx';

import { Box } from '@virginmediao2/momentum-nextjs/box';
import { Flex } from '@virginmediao2/momentum-nextjs/flex';
import { Text, TextColor } from '@virginmediao2/momentum-nextjs/text';
import React, { FC, ReactElement, useEffect, useState } from 'react';

import styles from './countdown.module.css';
import { getTimeRemaining, isPast } from './helpers';

type TextSize =
  | 'T500'
  | 'T300'
  | 'T200'
  | 'T400'
  | 'T600'
  | 'T700'
  | 'T800'
  | 'T900';

type TextWeight = 'light' | 'bold' | 'regular' | 'medium' | 'heavy';

type BoxBg =
  | 'warning'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'warning'
  | 'info'
  | 'success';

type BoxPadding = 'xs' | 'sm' | 'md' | 'lg';

type BoxRounding = 'xs' | 'sm' | 'md' | 'lg';

export interface CountdownBaseProps {
  hide_days?: boolean;
  hide_hours?: boolean;
  hide_mins?: boolean;
  automatic_update_mins?: number;
  number_colour?: TextColor;
  number_size?: TextSize;
  number_weight?: TextWeight;
  number_bg?: BoxBg;
  number_padding?: BoxPadding;
  number_rounding?: BoxRounding;
  text_colour?: TextColor;
  text_size?: TextSize;
  text_weight?: TextWeight;
}

const inlineCountdownContainer = (content: ReactElement, live: boolean) => {
  return (
    <Flex
      align='center'
      wrap='wrap'
      gap='sm'
      justify='center'
      data-testid='countdown'
      role='status'
      aria-live={live ? 'polite' : 'off'}
      aria-atomic='true'
    >
      {content}
    </Flex>
  );
};

interface CountDownProps extends CountdownBaseProps {
  endsDate: Date;
  now: Date;
  completeContent?: ReactElement;
  preContent?: ReactElement;
  postContent?: ReactElement;
  days_label: string;
  hours_label: string;
  mins_label: string;
}
export const Countdown: FC<CountDownProps> = (props) => {
  const {
    preContent,
    postContent,
    completeContent,
    now,
    endsDate,
    hide_days,
    hide_hours,
    hide_mins,
    automatic_update_mins,
    days_label,
    hours_label,
    mins_label,
    ...styleOptions
  } = props;

  const [timeRemaining, setTimeRemaining] = useState(
    getTimeRemaining(endsDate, now)
  );
  const [isComplete, setIsComplete] = useState(isPast(endsDate, now));
  const [elapsedIntervals, setElapsedIntervals] = useState(0);

  useEffect(() => {
    if (isComplete || !automatic_update_mins || automatic_update_mins <= 0)
      return;

    const updateInterval = automatic_update_mins * 60 * 1000;

    const interval = setInterval(() => {
      const newElapsedIntervals = elapsedIntervals + 1;
      setElapsedIntervals(newElapsedIntervals);
      const minutesToAdd = automatic_update_mins * newElapsedIntervals;

      const updatedNow = new Date(now);
      updatedNow.setMinutes(now.getMinutes() + minutesToAdd);

      const newTimeRemaining = getTimeRemaining(endsDate, updatedNow);
      setTimeRemaining(newTimeRemaining);

      if (isPast(endsDate, updatedNow)) {
        setIsComplete(true);
        clearInterval(interval);
      }
    }, updateInterval);

    return () => clearInterval(interval);
  }, [automatic_update_mins, endsDate, elapsedIntervals, now, isComplete]);

  if (isComplete) {
    return inlineCountdownContainer(completeContent || <></>, false);
  }

  const { days, hours, minutes } = timeRemaining;

  const parts: { label: string; value: string }[] = [];

  if (!hide_days) {
    parts.push({
      label: days_label,
      value: days,
    });
  }
  if (!hide_hours) {
    parts.push({
      label: hours_label,
      value: hours,
    });
  }
  if (!hide_mins) {
    parts.push({
      label: mins_label,
      value: minutes,
    });
  }

  const number_bg: BoxBg = styleOptions.number_bg || 'warning';
  const number_colour: TextColor = styleOptions.number_colour || 'light';
  const number_size: TextSize = styleOptions.number_size || 'T500';
  const number_weight: TextWeight = styleOptions.number_weight || 'bold';
  const number_padding: BoxPadding = styleOptions.number_padding || 'xs';
  const number_rounding: BoxRounding = styleOptions.number_rounding || 'sm';

  const text_colour: TextColor = styleOptions.text_colour || 'light';
  const text_size: TextSize = styleOptions.text_size || 'T300';
  const text_weight: TextWeight = styleOptions.text_weight || 'light';

  return inlineCountdownContainer(
    <>
      {preContent}
      <Flex
        className={clsx(styles['container'])}
        gap='sm'
        align='center'
        justify='center'
      >
        {parts.map((part) => (
          <React.Fragment key={part.label}>
            <Box
              data-testid='countdown_number_container'
              bg={number_bg}
              padding={number_padding}
              rounded={number_rounding}
              width='auto'
            >
              <Text
                data-testid='countdown_number'
                color={number_colour}
                as='span'
                weight={number_weight}
                size={number_size}
              >
                {part.value}
              </Text>
            </Box>
            <Text
              data-testid='countdown_label'
              color={text_colour}
              size={text_size}
              weight={text_weight}
            >
              {part.label}
            </Text>
          </React.Fragment>
        ))}
      </Flex>
      {postContent}
    </>,
    !!(automatic_update_mins && automatic_update_mins > 0)
  );
};
